import { getFormattedUnit } from "config";
import ExcelJS from "exceljs";

const htmlToText = (html) => {
  if (!html) return "-";

  // decode HTML entities
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  let decoded = textarea.value;

  return (
    decoded
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?p>/gi, "\n")
      .replace(/<\/?div>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim() || "-"
  );
};
const applyAutoRowHeight = (
  sheet,
  rowIndex,
  text,
  { mergedCols = 3, columnWidth = 15, minHeight = 22, lineHeight = 15 } = {}
) => {
  if (!text || text === "-") return;

  const approxCharsPerLine = Math.floor(columnWidth * mergedCols * 1.2);
  const lineCount = Math.ceil(text.length / approxCharsPerLine);

  const row = sheet.getRow(rowIndex);
  row.height = Math.max(minHeight, lineCount * lineHeight);
};
const getCleanRemarkText = (html) => {
  const text = htmlToText(html);
  return text === "-" ? "" : text;
};

const getWrappedTextHeight = (
  texts,
  { mergedCols = 3, columnWidth = 15, minHeight = 24, lineHeight = 16 } = {}
) => {
  const approxCharsPerLine = Math.max(
    20,
    Math.floor(columnWidth * mergedCols * 1.15)
  );
  const lineCount = texts.reduce((maxLines, text) => {
    const currentLines = String(text || "")
      .split("\n")
      .reduce(
        (total, line) =>
          total + Math.max(1, Math.ceil(line.length / approxCharsPerLine)),
        0
      );

    return Math.max(maxLines, currentLines);
  }, 1);

  return Math.max(minHeight, lineCount * lineHeight + 8);
};
const applyOuterBorderToRange = (sheet, lastRow) => {
  try {
    const lastCol = sheet.columnCount || 1;

    // Only apply borders if cells exist
    if (lastRow > 0 && lastCol > 0) {
      // Apply borders carefully
      for (let c = 1; c <= lastCol; c++) {
        // Top border
        const topCell = sheet.getRow(1).getCell(c);
        topCell.border = {
          ...topCell.border,
          top: { style: "thin", color: { argb: "FF000000" } },
        };

        // Bottom border
        const bottomCell = sheet.getRow(lastRow).getCell(c);
        if (bottomCell) {
          bottomCell.border = {
            ...bottomCell.border,
            bottom: { style: "thin", color: { argb: "FF000000" } },
          };
        }
      }
    }
  } catch (error) {
    console.warn("Border application error:", error);
  }
};
const getRankNumber = (rank) => {
  if (!rank || rank === "NA") return Infinity; // push to last
  const num = parseInt(rank.replace(/\D/g, ""), 10);
  return isNaN(num) ? Infinity : num;
};
const hasValidBudget = (rfqData) => {
  const budget = rfqData?.rfqBudget;
  return typeof budget === "number" && !isNaN(budget) && budget > 0;

};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
};

const getNegotiationOutcome = (supplier) => {
  const quotationSets = supplier?.vendorQuotations?.quotationSets || [];
  const firstQuotation = quotationSets[0];
  const lastQuotation = quotationSets[quotationSets.length - 1];
  const firstTotal =
    (firstQuotation?.totalWithoutGst || 0) +
    (firstQuotation?.othersTotalWithoutGst || 0);
  const lastTotal =
    (lastQuotation?.totalWithoutGst || 0) +
    (lastQuotation?.othersTotalWithoutGst || 0);

  if (firstTotal === lastTotal) return "Not Updated";

  const difference = firstTotal - lastTotal;
  const negotiationPercentage =
    firstTotal > 0 ? ((difference / firstTotal) * 100).toFixed(2) : 0;

  return `Rs. ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(difference)} (${negotiationPercentage}%)`;
};

const setAmountCell = (cell, value) => {
  if (value === "-" || value === null || value === undefined || isNaN(value)) {
    cell.value = "-";
    cell.alignment = { horizontal: "right", vertical: "middle" };
    return;
  }

  cell.value = Number(value); // THIS fixes string issue
  cell.numFmt = "#,##0.00";
  cell.alignment = { horizontal: "right", vertical: "middle" };
};

const getLatestQuotationSet = (supplier) =>
  supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || {};

const getCommercialDiscountPercent = (supplier) => {
  const percent = Number(getLatestQuotationSet(supplier)?.discountOnBasicAmountPercent || 0);
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  return Math.min(100, percent);
};

const getCommercialDiscountAmount = (supplier) => {
  const amount = Number(getLatestQuotationSet(supplier)?.discountOnBasicAmount || 0);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return amount;
};

const hasCommercialDiscount = (rfqData, suppliers = []) =>
  rfqData?.rfqType === "material" &&
  rfqData?.status === "CLOSED" &&
  suppliers.some((supplier) => getCommercialDiscountPercent(supplier) > 0 || getCommercialDiscountAmount(supplier) > 0);

const addVerticalSeparators = (
  sheet,
  {
    lastRow,
    staticEndCol = 7, // Column E
    vendorStartCol = 8, // Column F
    vendorColSpan = 3, // 3 columns per vendor
    vendorCount,
  }
) => {
  const rows = sheet.getRows(1, lastRow);

  // 1️ Border after column E
  rows.forEach((row) => {
    const cell = row.getCell(staticEndCol);
    cell.border = {
      ...cell.border,
      right: { style: "medium", color: { argb: "FF808080" } },
    };
  });

  // 2️ Border after each vendor block
  for (let i = 0; i < vendorCount; i++) {
    const endCol = vendorStartCol + i * vendorColSpan + (vendorColSpan - 1);

    rows.forEach((row) => {
      const cell = row.getCell(endCol);
      cell.border = {
        ...cell.border,
        right: { style: "medium", color: { argb: "FF808080" } },
      };
    });
  }
};

export const handleDownloadComparisonExcel = async (
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar
) => {
  // Include ALL suppliers (both with and without quotations)
  const allSuppliers = rfqData?.suppliers || [];

  // Separate suppliers into two groups
  const suppliersWithQuotations = allSuppliers.filter(
    (supplier) => supplier?.quotationReceived
  );

  const suppliersWithoutQuotations = allSuppliers.filter(
    (supplier) => !supplier?.quotationReceived
  );

  // Sort only suppliers with quotations by rank
  const sortedSuppliersWithQuotes = [...suppliersWithQuotations].sort(
    (a, b) => {
      const rankA = a?.vendorQuotations?.rank;
      const rankB = b?.vendorQuotations?.rank;

      // NA or missing ranks go to bottom
      if (!rankA || rankA === "NA") return 1;
      if (!rankB || rankB === "NA") return -1;

      const numA = parseInt(rankA?.replace("L", ""), 10);
      const numB = parseInt(rankB?.replace("L", ""), 10);

      return numA - numB;
    }
  );

  // Combine sorted suppliers with quotes and unsorted suppliers without quotes
  const sortedSuppliers = [
    ...sortedSuppliersWithQuotes,
    ...suppliersWithoutQuotations,
  ];

  /*
  const budgetDeviationText = `Rs. ${formattedDiff || "NA"} (${budgetDeviationPercentage.toFixed(
    2
  )}%) ${
    isOverBudget ? "▲" : "▼"
  }`;

  */
  const shortLowestNetRates = {};

  rfqData.materials.forEach((material) => {
    let minRate = Infinity;

    sortedSuppliers.forEach((supplier) => {
      if (!supplier.quotationReceived) return;

      const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

      const item = lastSet?.materialDetails?.find((m) => m.id === material._id);

      if (!item || !item.basePrice) return;

      const netRate = item.basePrice - (item.basePrice * item.discount) / 100;

      if (netRate > 0 && netRate < minRate) {
        minRate = netRate;
      }
    });

    shortLowestNetRates[material._id] = minRate === Infinity ? null : minRate;
  });

  try {
    setExcelLoading(true);
    setLoadingMessage("Generating Comparison Excel...");

    const workbook = new ExcelJS.Workbook();

    // Now Add "Other Charges"
    const chargeLabels = [
      { label: "Transport Charges :", key: "transport" },
      { label: "Mathade Charges :", key: "mathadeCharges" },
      { label: "Loading Charges :", key: "loading" },
      { label: "Unloading Charges :", key: "unLoading" },
      { label: "Miscellaneous Charges :", key: "miscCharges" },
    ];
    const enabledCharges = chargeLabels.filter(
      (charge) => rfqData?.otherCharges?.[charge.key]
    );
    const hasMaterialCode = rfqData?.materials?.some((material) =>
      String(material?.code || "").trim()
    );
    const showQuotedAmount = ["ent_2", "ent_35"].includes(
      String(rfqData?.entityId || "")
    );
    const shortVendorColumnCount = showQuotedAmount ? 4 : 3;
    const shortNegotiatedRateOffset = showQuotedAmount ? 2 : 1;
    const shortNegotiatedAmountOffset = showQuotedAmount ? 3 : 2;

    const shortSheet = workbook.addWorksheet("Summary View");

    const shortStaticColumnCount = hasMaterialCode ? 9 : 8;
    const shortStaticEndLetter = shortSheet.getColumn(shortStaticColumnCount).letter;
    const shortInfoLeftEndLetter = hasMaterialCode ? "E" : "D";
    const shortInfoRightStartLetter = hasMaterialCode ? "F" : "E";

    // Column widths
    shortSheet.columns = [
      { width: 5 }, // A - Sr
      ...(hasMaterialCode ? [{ width: 14 }] : []), // B - Code
      { width: 35 }, // Material
      { width: 10 }, // Qty
      { width: 10 }, // Unit
      { width: 15 }, // Last Rate
      { width: 15 }, // Budget
      { width: 18 }, // Budget Amount
      { width: 15 }, // Lowest Rate
    ];

    // ---------------- RFQ INFO (ROWS 1-3) ----------------
    // Keep the RFQ metadata in its original two-column layout while vendors use
    // the parallel merged space to the right.
    [
      [
        `Project Name: ${rfqData.projectName || "-"}`,
        `RFQ ID: ${rfqData.rfqId || "-"}`,
      ],
      [
        `RFQ Name: ${rfqData?.name || "-"}`,
        `Created By: ${rfqData?.requesterName || "-"}`,
      ],
      [
        `RFQ Type: ${rfqData.rfqType || "-"}`,
        `Created At: ${formatDate(rfqData.createdAt)}`,
      ],
    ].forEach(([leftValue, rightValue], index) => {
      const rowNumber = index + 1;
      shortSheet.mergeCells(`A${rowNumber}:${shortInfoLeftEndLetter}${rowNumber}`);
      shortSheet.mergeCells(
        `${shortInfoRightStartLetter}${rowNumber}:${shortStaticEndLetter}${rowNumber}`
      );

      [
        shortSheet.getCell(`A${rowNumber}`),
        shortSheet.getCell(`${shortInfoRightStartLetter}${rowNumber}`),
      ].forEach((cell, cellIndex) => {
        cell.value = cellIndex === 0 ? leftValue : rightValue;
        cell.font = { bold: true };
        cell.alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };
      });
      shortSheet.getRow(rowNumber).height = 22;
    });

    // ---------------- VENDOR HEADERS (ROW 1) ----------------

    let shortStartCol = shortStaticColumnCount + 1;
    const getShortVendorStartCol = (supplierIndex) =>
      shortStartCol + supplierIndex * shortVendorColumnCount;
    const getShortVendorEndCol = (supplierIndex) =>
      getShortVendorStartCol(supplierIndex) + shortVendorColumnCount - 1;

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

      shortSheet.mergeCells(`${startLetter}1:${endLetter}1`);
      shortSheet.mergeCells(`${startLetter}2:${endLetter}2`);

      const businessNameCell = shortSheet.getCell(`${startLetter}1`);
      const contactCell = shortSheet.getCell(`${startLetter}2`);
      const mobile = supplier.contactDetails?.[0]?.mobile ?? "-";
      const email = supplier.contactDetails?.[0]?.email ?? "-";
      const rank = supplier.vendorQuotations?.rank ?? "NA";
      businessNameCell.value = `${supplier.businessName || "-"} (${rank})`;
      contactCell.value = `${mobile} | ${email}`;

      [businessNameCell, contactCell].forEach((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          wrapText: true,
          vertical: "middle",
          horizontal: "center",
        };
      });
    });

    // Ensure equal width for vendor columns.
    sortedSuppliers.forEach((_, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);

      shortSheet.getColumn(colStart).width = 14; // Quoted Rate
      if (showQuotedAmount) {
        shortSheet.getColumn(colStart + 1).width = 16; // Quoted Amount
      }
      shortSheet.getColumn(colStart + shortNegotiatedRateOffset).width = 14;
      shortSheet.getColumn(colStart + shortNegotiatedAmountOffset).width = 16;
    });

    // ---------------- RFQ AND VENDOR BUDGET ROW (ROW 4) ----------------
    shortSheet.mergeCells(`A4:${shortInfoLeftEndLetter}4`);
    shortSheet.mergeCells(
      `${shortInfoRightStartLetter}4:${shortStaticEndLetter}4`
    );
    const indentCell = shortSheet.getCell("A4");
    indentCell.value = `Indent No.: ${rfqData?.parentsRefId || "-"}`;
    indentCell.font = { bold: true };
    indentCell.alignment = { horizontal: "left", vertical: "middle" };

    const rfqBudgetCell = shortSheet.getCell(`${shortInfoRightStartLetter}4`);
    rfqBudgetCell.value = `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
      rfqData?.rfqBudget || 0
    )}`;
    rfqBudgetCell.font = { bold: true };
    rfqBudgetCell.alignment = { horizontal: "left", vertical: "middle" };
    shortSheet.getRow(4).height = 24;

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;
      const budgetCell = shortSheet.getCell(`${startLetter}4`);
      const totalWithoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;
      const deviation = (rfqData?.rfqBudget ?? 0) - totalWithoutGst;
      const hasBudgetDeviation = supplier.quotationReceived && hasValidBudget(rfqData);
      const deviationPercentage = hasBudgetDeviation
        ? Math.abs((deviation / rfqData.rfqBudget) * 100)
        : 0;

      shortSheet.mergeCells(`${startLetter}4:${endLetter}4`);
      budgetCell.value = hasBudgetDeviation
        ? `Rs. ${new Intl.NumberFormat("en-IN").format(
            Math.abs(deviation)
          )} (${deviationPercentage.toFixed(2)}%) ${deviation < 0 ? "▲" : "▼"}`
        : "";
      budgetCell.font = hasBudgetDeviation
        ? {
            bold: true,
            color: { argb: deviation < 0 ? "FFFF0000" : "FF008000" },
          }
        : {};
      budgetCell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });

    // ---------------- TABLE HEADERS (ROW 5) ----------------

    const headerRow = shortSheet.getRow(5);
    headerRow.height = 42;

    shortSheet.getRow(5).values = [
      "Sr.",
      ...(hasMaterialCode ? ["Code"] : []),
      "Material Name",
      "Qty",
      "Unit",
      "Last Rate",
      "Budget",
      "Budget Amount",
      "Lowest Rate",
    ];

    Array.from({ length: shortStaticColumnCount }, (_, index) => index + 1).forEach((c) => {
      headerRow.getCell(c).font = { bold: true };
      headerRow.getCell(c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" }, // light grey
      };
      headerRow.getCell(c).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
    });

    // Vendor headers (E onwards)
    sortedSuppliers.forEach((_, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);

      const setHeaderCell = (cell, text) => {
        cell.value = text;
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" }, // light grey
        };
        cell.alignment = {
          wrapText: true,
          horizontal: "center",
          vertical: "middle",
        };
      };
 
      setHeaderCell(headerRow.getCell(colStart), "Quoted\nRate");

      if (showQuotedAmount) {
        setHeaderCell(headerRow.getCell(colStart + 1), "Quoted\nAmount");
      }

      setHeaderCell(
        headerRow.getCell(colStart + shortNegotiatedRateOffset),
        "Negotiated\nRate"
      );

      setHeaderCell(
        headerRow.getCell(colStart + shortNegotiatedAmountOffset),
        "Negotiated\nAmount"
      );
    });

    // ---------------- MATERIAL ROWS (START FROM ROW 6) ----------------
    let currentRow = 6;

    rfqData.materials.forEach((material, index) => {
      const hasAnyRemark = sortedSuppliers.some((supplier) => {
        if (!supplier.quotationReceived) return false;

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const item = lastSet?.materialDetails?.find(
          (m) => m.id === material._id
        );

        return Boolean(getCleanRemarkText(item?.remark));
      });

      const baseRow = currentRow;
      const rateRow = shortSheet.getRow(baseRow);
      const remarkRow = hasAnyRemark
        ? shortSheet.getRow(baseRow + 1)
        : null;
      const shortMaterialNameCol = hasMaterialCode ? 3 : 2;
      const shortQtyCol = hasMaterialCode ? 4 : 3;
      const shortUnitCol = hasMaterialCode ? 5 : 4;
      const shortLastRateCol = hasMaterialCode ? 6 : 5;
      const shortBudgetCol = hasMaterialCode ? 7 : 6;
      const shortBudgetAmountCol = hasMaterialCode ? 8 : 7;
      const shortLowestRateCol = hasMaterialCode ? 9 : 8;

      // -------- RATE ROW --------
      rateRow.getCell(1).value = index + 1;
      if (hasMaterialCode) {
        rateRow.getCell(2).value = material.code || "-";
      }
      rateRow.getCell(shortMaterialNameCol).value = htmlToText(material.name);
      rateRow.getCell(shortQtyCol).value = material.quantity;
      rateRow.getCell(shortUnitCol).value = getFormattedUnit(material.unit);
      // Last Rate → Column E (5)
      if (material.lastRate) {
        setAmountCell(rateRow.getCell(shortLastRateCol), material.lastRate);
      } else {
        rateRow.getCell(shortLastRateCol).value = "-";
        rateRow.getCell(shortLastRateCol).alignment = {
          horizontal: "center",
          vertical: "middle",
        };
      }

      // Budget → Column F (6)
      setAmountCell(rateRow.getCell(shortBudgetCol), material?.budget);
      rateRow.getCell(shortBudgetCol).alignment = { horizontal: "right", vertical: "middle" };

      // Budget Amount → Column G (7)
      setAmountCell(rateRow.getCell(shortBudgetAmountCol), material?.budgetAmount);
      rateRow.getCell(shortBudgetAmountCol).alignment = { horizontal: "right", vertical: "middle" };

      // Lowest Rate → Column H (8)
      setAmountCell(rateRow.getCell(shortLowestRateCol), shortLowestNetRates[material._id]);
      rateRow.getCell(shortLowestRateCol).alignment = { horizontal: "right", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = getShortVendorStartCol(supplierIndex);

        if (!supplier.quotationReceived) {
          for (let offset = 0; offset < shortVendorColumnCount; offset += 1) {
            rateRow.getCell(colStart + offset).value = "-";
          }
          return;
        }

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const firstSet = supplier.vendorQuotations?.quotationSets?.[0];

        const lastItem = lastSet?.materialDetails?.find(
          (m) => m.id === material._id
        );
        const firstItem = firstSet?.materialDetails?.find(
          (m) => m.id === material._id
        );

        const netRate = lastItem
          ? lastItem.basePrice - (lastItem.basePrice * lastItem.discount) / 100
          : 0;

        const firstRate = firstItem
          ? firstItem.basePrice -
            (firstItem.basePrice * firstItem.discount) / 100
          : 0;

        // ----- First Rate (no highlight) -----
        setAmountCell(rateRow.getCell(colStart), firstRate);

        if (showQuotedAmount) {
          setAmountCell(rateRow.getCell(colStart + 1), firstItem?.priceWithoutGst);
        }

        // ----- Current / Negotiation Rate (HIGHLIGHT LOWEST) -----
        const currentRateCell = rateRow.getCell(
          colStart + shortNegotiatedRateOffset
        );
        const currentRate = Number(netRate);

        // 1️ Set value first
        setAmountCell(currentRateCell, currentRate);

        // 2️ Highlight lowest current rate
        const lowestRate = shortLowestNetRates[material._id];

        if (
          currentRate > 0 &&
          lowestRate != null &&
          Math.abs(currentRate - lowestRate) < 0.000001
        ) {
          currentRateCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFBBF7B3" }, // green
          };
        }

        // ----- Negotiated Amount -----
        setAmountCell(
          rateRow.getCell(colStart + shortNegotiatedAmountOffset),
          lastItem?.priceWithoutGst
        );
      });

      // -------- REMARK ROW --------
      if (hasAnyRemark) {
        const remarkRowIndex = baseRow + 1;
        shortSheet.mergeCells(
          `B${remarkRowIndex}:${shortStaticEndLetter}${remarkRowIndex}`
        );
        const remarkLabelCell = shortSheet.getCell(`B${remarkRowIndex}`);
        // remarkLabelCell.value = "Vendor Remarks:";
        remarkLabelCell.font = { bold: true, italic: true };
        remarkLabelCell.alignment = {
          wrapText: true,
          vertical: "top",
          horizontal: "left",
        };

        sortedSuppliers.forEach((supplier, supplierIndex) => {
          if (!supplier.quotationReceived) return;

          const colStart = getShortVendorStartCol(supplierIndex);
          const lastSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

          const item = lastSet?.materialDetails?.find(
            (m) => m.id === material._id
          );

          if (!getCleanRemarkText(item?.remark)) return;

          const startLetter = shortSheet.getColumn(colStart).letter;
          const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

          shortSheet.mergeCells(
            `${startLetter}${remarkRowIndex}:${endLetter}${remarkRowIndex}`
          );

          const cell = shortSheet.getCell(`${startLetter}${remarkRowIndex}`);
          cell.value = getCleanRemarkText(item.remark);
          cell.font = { italic: true };
          cell.alignment = {
            wrapText: true,
            vertical: "top",
            horizontal: "left",
          };

        });

        const remarks = sortedSuppliers.map((supplier) => {
          const lastSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
          const item = lastSet?.materialDetails?.find((m) => m.id === material._id);
          return getCleanRemarkText(item?.remark);
        });
        remarkRow.height = getWrappedTextHeight(remarks, {
          mergedCols: shortVendorColumnCount,
          // Excel wraps italic remark text sooner than the nominal column width.
          columnWidth: 10,
          minHeight: 32,
          lineHeight: 20,
        }) + 8;
      }

      currentRow += 1 + (hasAnyRemark ? 1 : 0);
    });

    // row 7
    // ================= SUMMARY & FOOTER ROWS =================
    const hasAnyPaymentTerms = sortedSuppliers.some((supplier) => {
      if (!supplier.quotationReceived) return false;

      const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

      return !!rfqData?.paymentTerms || !!lastSet?.counterPaymentTerms;
    });

    const hasAnyVendorNotes = sortedSuppliers.some((supplier) => {
      if (!supplier.quotationReceived) return false;

      return !!supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes;
    });

    let summaryStartRow = currentRow + 1;

    // material cost
    shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value = "Material Cost :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);

      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cost =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.totalWithoutGst ?? "-";

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        cost
      );
      shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };
    });

    if (hasCommercialDiscount(rfqData, sortedSuppliers)) {
      summaryStartRow += 1;

      shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value =
        "Commercial Discount :";
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };
      shortSheet.getCell(`A${summaryStartRow}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFF2CC" },
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = getShortVendorStartCol(supplierIndex);
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;
        const percent = getCommercialDiscountPercent(supplier);
        const amount = getCommercialDiscountAmount(supplier);

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
        cell.value = amount > 0
          ? `Rs. ${new Intl.NumberFormat("en-IN", {
              maximumFractionDigits: 2,
            }).format(amount)}${percent > 0 ? ` (${percent}%)` : ""}`
          : "-";
        cell.alignment = { horizontal: "right", vertical: "middle" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFF2CC" },
        };
      });
    }

    // ---------- OTHER CHARGES ----------

    enabledCharges.forEach((charge) => {
      const hasAnyChargeValue = sortedSuppliers.some((supplier) => {
        if (!supplier.quotationReceived) return false;

        const value =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;

        return Number(value) > 0;
      });

      //If all vendors have 0 → skip row
      const chargeRemarks = sortedSuppliers.map((supplier, supplierIndex) => ({
        supplierIndex,
        text: supplier.quotationReceived
          ? getCleanRemarkText(
              supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
                ?.otherCharges?.[charge.key]?.remark
            )
          : "",
      }));
      const validChargeRemarks = chargeRemarks.filter(({ text }) => !!text);

      if (!hasAnyChargeValue && validChargeRemarks.length === 0) return;

      summaryStartRow += 1;

      // ---- LABEL (A–E merged) ----
      shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value = charge.label;
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      // ---- VALUES (PER VENDOR, MERGED 3 COLUMNS) ----
      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = getShortVendorStartCol(supplierIndex);
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const chargeValue =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;

        setAmountCell(
          shortSheet.getCell(`${startLetter}${summaryStartRow}`),
          chargeValue
        );
      });

      if (validChargeRemarks.length > 0) {
        summaryStartRow += 1;

        shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
        const labelCell = shortSheet.getCell(`A${summaryStartRow}`);
        labelCell.value = "Remarks :";
        labelCell.font = { italic: true };
        labelCell.alignment = {
          horizontal: "right",
          vertical: "top",
        };

        chargeRemarks.forEach(({ supplierIndex, text }) => {
          const colStart = getShortVendorStartCol(supplierIndex);
          const startLetter = shortSheet.getColumn(colStart).letter;
          const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

          shortSheet.mergeCells(
            `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
          );

          const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
          cell.value = text || "";
          cell.font = { italic: true };
          cell.alignment = {
            wrapText: true,
            vertical: "top",
            horizontal: "left",
          };
        });

        shortSheet.getRow(summaryStartRow).height = getWrappedTextHeight(
          validChargeRemarks.map(({ text }) => text),
          {
            mergedCols: shortVendorColumnCount,
            columnWidth: 15,
            minHeight: 24,
            lineHeight: 16,
          }
        );
      }
    });

    // ---------- Total Basic Amount ----------
    summaryStartRow += 1;

    // Merge label A–E
    shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value = "Total Basic Amount :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };
    shortSheet.getCell(`A${summaryStartRow}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9D9D9" }, // light grey
    };

    // Merge each vendor's Summary View columns.
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const value = supplier?.vendorQuotations?.grandTotalWithoutGst ?? "-";

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        value
      );

      shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };
      shortSheet.getCell(`${startLetter}${summaryStartRow}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" }, // light grey
      };
    });

    // ---------- Total GST ----------
    summaryStartRow += 1;

    // Merge label A–E
    shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value = "Total GST :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    // Merge each vendor's Summary View columns.
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const withGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
      const withoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;
      const gstAmount = withGst - withoutGst;

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        isNaN(gstAmount) ? "-" : gstAmount
      );

      shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };
    });

    // ---------- Total Quotation Amount (Incl. GST) ----------
    summaryStartRow += 1;

    // Merge label A–E
    shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
    // Set value
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Total Quotation Value (Incl. GST) :";

    // Apply grey background to the entire merged row (A–E)
    ["A", "B", "C", "D", "E", "F", "G", "H"].forEach((col) => {
      const cell = shortSheet.getCell(`${col}${summaryStartRow}`);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" }, // light grey
      };
    });

    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    // Merge each vendor's Summary View columns.
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const totalWithGst = supplier?.vendorQuotations?.grandTotalWithGst ?? "-";

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        totalWithGst
      );

      shortSheet.getCell(`${startLetter}${summaryStartRow}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" }, // light grey
      };
    });

    // ---------- Difference from Lowest Quote ----------
    summaryStartRow += 1;

    // Merge label A–E
    shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Difference from Lowest Quote :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    // L1 reference (lowest total without GST)
    const shortL1Total =
      sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    // Merge each vendor's Summary View columns.
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);

      if (supplierIndex === 0) {
        cell.value = "Lowest Quote";
        cell.font = { italic: true };
      } else if (!supplier.quotationReceived) {
        cell.value = "Quotation Not Submitted";
      } else {
        const currentTotal =
        supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

      const diff = currentTotal - shortL1Total;

      const deviationPercent = shortL1Total
          ? ((diff / shortL1Total) * 100).toFixed(2)
          : 0;
        cell.value = `Rs. ${new Intl.NumberFormat("en-IN").format(
          diff
        )} (${deviationPercent}%)`;
      }

      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
        wrapText: true,
      };
    });

    // ---------- Negotiation Outcome ----------
    summaryStartRow += 1;
    shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Negotiation Outcome :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = getShortVendorStartCol(supplierIndex);
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;
      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
      cell.value = supplier.quotationReceived
        ? getNegotiationOutcome(supplier)
        : "Quotation Not Submitted";
      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
        wrapText: true,
      };
    });

    // ---------- Payment Terms ----------
    if (hasAnyPaymentTerms) {
      summaryStartRow += 1;

      shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value = "Payment Terms :";
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = getShortVendorStartCol(supplierIndex);
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const text = lastSet?.agreedToPaymentTerms
          ? htmlToText(rfqData?.paymentTerms)
          : htmlToText(lastSet?.counterPaymentTerms) || "-";

        const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
        cell.value = text;
        cell.alignment = {
          horizontal: "left",
          vertical: "top",
          wrapText: true,
        };

        // AUTO HEIGHT
        applyAutoRowHeight(shortSheet, summaryStartRow, text, {
          mergedCols: shortVendorColumnCount,
          columnWidth: 15,
        });
      });
    }

    // ---------- Vendor Notes ----------
    if (hasAnyVendorNotes) {
      summaryStartRow += 1;

      shortSheet.mergeCells(`A${summaryStartRow}:${shortStaticEndLetter}${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value = "Vendor Notes :";
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = getShortVendorStartCol(supplierIndex);
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(getShortVendorEndCol(supplierIndex)).letter;

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const text =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";

        const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
        cell.value = text;
        cell.alignment = {
          horizontal: "left",
          vertical: "top",
          wrapText: true,
        };

        // AUTO HEIGHT
        applyAutoRowHeight(shortSheet, summaryStartRow, text, {
          mergedCols: shortVendorColumnCount,
          columnWidth: 15,
        });
      });
    }

    addVerticalSeparators(shortSheet, {
      lastRow: summaryStartRow,
      staticEndCol: shortStaticColumnCount,
      vendorStartCol: shortStartCol,
      vendorColSpan: shortVendorColumnCount,
      vendorCount: sortedSuppliers.length,
    });

    // FORCE border till Vendor Notes row
    applyOuterBorderToRange(shortSheet, summaryStartRow);

    const sheet = workbook.addWorksheet("Detailed View");

    const setNumericCell = (cell, value) => {
      cell.value = value ?? 0;
      cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    };

    const calculatePercentageDifference = (firstPrice, latestPrice) => {
      if (!firstPrice || firstPrice === 0) return 0;
      return latestPrice - firstPrice;
    };

    const detailedStaticColumnCount = hasMaterialCode ? 12 : 11;
    const detailedStaticEndLetter = sheet.getColumn(detailedStaticColumnCount).letter;
    const detailedInfoLeftEndCol = Math.ceil(detailedStaticColumnCount / 2);
    const detailedInfoLeftEndLetter = sheet.getColumn(detailedInfoLeftEndCol).letter;
    const detailedInfoRightStartCol = detailedInfoLeftEndCol + 1;
    const detailedInfoRightStartLetter = sheet.getColumn(detailedInfoRightStartCol).letter;
    const detailedVendorColumnCount = 7;

    sheet.columns = [
      { header: "Sr.", key: "sr", width: 5 },
      ...(hasMaterialCode ? [{ header: "Code", key: "code", width: 14 }] : []),
      { header: "Material Name", key: "materialName", width: 35 },
      { header: "Specifications", key: "specifications", width: 30 },
      { header: "Approved Brands", key: "brands", width: 20 },
      { header: "Qty", key: "quantity", width: 10 },
      { header: "Unit", key: "unit", width: 10 },
      { header: "Budget", width: 15 },
  { header: "Budget Amount", width: 18 },
      { header: "Last Rate", key: "lastRate", width: 15 },
      { header: "Last Rate Date", key: "lastRateDate", width: 15 },
      { header: "Lowest Rate", key: "lowestRate", width: 15 },
    ];
    [
      [
        `Project Name: ${rfqData?.projectName || "-"}`,
        `RFQ ID: ${rfqData?.rfqId || "-"}`,
      ],
      [
        `RFQ Name: ${rfqData?.name || "-"}`,
        `Created By: ${rfqData?.requesterName || "-"}`,
      ],
      [
        `RFQ Type: ${rfqData?.rfqType || "Material"}`,
        `Created At: ${formatDate(rfqData?.createdAt)}`,
      ],
    ].forEach(([leftValue, rightValue], index) => {
      const rowNumber = index + 1;
      sheet.mergeCells(`A${rowNumber}:${detailedInfoLeftEndLetter}${rowNumber}`);
      sheet.mergeCells(
        `${detailedInfoRightStartLetter}${rowNumber}:${detailedStaticEndLetter}${rowNumber}`,
      );

      [sheet.getCell(`A${rowNumber}`), sheet.getCell(`${detailedInfoRightStartLetter}${rowNumber}`)].forEach(
        (cell, cellIndex) => {
          cell.value = cellIndex === 0 ? leftValue : rightValue;
          cell.font = { bold: true };
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
          };
        },
      );
      sheet.getRow(rowNumber).height = 22;
    });

    sheet.mergeCells(`A4:${detailedInfoLeftEndLetter}4`);
    sheet.mergeCells(
      `${detailedInfoRightStartLetter}4:${detailedStaticEndLetter}4`,
    );
    sheet.getCell("A4").value = `Total Quotations: ${suppliersWithQuotations.length}`;
    sheet.getCell(`${detailedInfoRightStartLetter}4`).value =
      `Total Invited Vendors: ${allSuppliers.length}`;

    sheet.mergeCells(`A5:${detailedInfoLeftEndLetter}5`);
    sheet.mergeCells(
      `${detailedInfoRightStartLetter}5:${detailedStaticEndLetter}5`,
    );
    sheet.getCell("A5").value = `Indent No.: ${rfqData?.parentsRefId || "-"}`;
    sheet.getCell(`${detailedInfoRightStartLetter}5`).value =
      `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
        rfqData?.rfqBudget || 0,
      )}`;

    ["A4", `${detailedInfoRightStartLetter}4`, "A5", `${detailedInfoRightStartLetter}5`].forEach(
      (cell) => {
        sheet.getCell(cell).font = { bold: true };
        sheet.getCell(cell).alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };
      },
    );
    sheet.getRow(4).height = 22;
    sheet.getRow(5).height = 24;

    sheet.getRow(6).values = [
  "Sr.",
  ...(hasMaterialCode ? ["Code"] : []),
  "Material Name",
  "Specifications",
  "Approved Brands",
  "Qty",
  "Unit",
  "Budget",
  "Budget Amount",
  "Last Rate",
  "Last Rate Date",
  "Lowest Rate",
];
    sheet.getRow(6).font = { bold: true };
    sheet.getRow(6).alignment = { vertical: "middle", horizontal: "center" };

    const lowestPrices = {};
    rfqData?.materials?.forEach((material) => {
      let minPrice = Infinity;
      let minSupplierIndexes = [];

      sortedSuppliersWithQuotes?.forEach((supplier, vendorIndex) => {
        const vendorQuotation =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const vendorItem = vendorQuotation?.materialDetails?.find(
          (item) => item.id === material._id
        );

        if (vendorItem && vendorItem.basePrice > 0) {
          const discountedPrice =
            vendorItem.basePrice -
            (vendorItem.basePrice * vendorItem.discount) / 100;
          if (discountedPrice > 0) {
            if (discountedPrice < minPrice) {
              minPrice = discountedPrice;
              minSupplierIndexes = [vendorIndex];
            } else if (discountedPrice === minPrice) {
              minSupplierIndexes.push(vendorIndex);
            }
          }
        }
      });

      lowestPrices[material._id] = {
        minPrice: minPrice === Infinity ? 0 : minPrice,
        minSupplierIndexes,
      };
    });

    let detailedCurrentRow = 7;
    const detailedMaterialRows = rfqData.materials.map((material) => {
      const vendorRemarks = sortedSuppliers.map((supplier) => {
        if (!supplier.quotationReceived) return "";

        const latestSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const item = latestSet?.materialDetails?.find((detail) => detail.id === material._id);
        return getCleanRemarkText(item?.remark);
      });
      const layout = {
        material,
        itemRow: detailedCurrentRow,
        vendorRemarks,
        vendorRemarksRow: null,
      };

      detailedCurrentRow += 1;
      if (vendorRemarks.some(Boolean)) {
        layout.vendorRemarksRow = detailedCurrentRow;
        detailedCurrentRow += 1;
      }

      return layout;
    });

    detailedMaterialRows.forEach(({ material, itemRow }, index) => {
  const row = sheet.getRow(itemRow);
  const detailedMaterialNameCol = hasMaterialCode ? 3 : 2;
  const detailedSpecificationCol = hasMaterialCode ? 4 : 3;
  const detailedBrandsCol = hasMaterialCode ? 5 : 4;
  const detailedQtyCol = hasMaterialCode ? 6 : 5;
  const detailedUnitCol = hasMaterialCode ? 7 : 6;
  const detailedBudgetCol = hasMaterialCode ? 8 : 7;
  const detailedBudgetAmountCol = hasMaterialCode ? 9 : 8;
  const detailedLastRateCol = hasMaterialCode ? 10 : 9;
  const detailedLastRateDateCol = hasMaterialCode ? 11 : 10;
  const detailedLowestRateCol = hasMaterialCode ? 12 : 11;

  row.getCell(1).value = index + 1;
  if (hasMaterialCode) {
    row.getCell(2).value = material.code || "-";
  }
  row.getCell(detailedMaterialNameCol).value = htmlToText(material.name);
  row.getCell(detailedSpecificationCol).value =
    getCleanRemarkText(material.specifications) || "-";
  row.getCell(detailedSpecificationCol).alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: true,
  };
  row.getCell(detailedBrandsCol).value = material.brands.join(", ");
  setNumericCell(row.getCell(detailedQtyCol), Number(material.quantity));
  row.getCell(detailedUnitCol).value = getFormattedUnit(material.unit);

  //  Budget (NEW)
  setNumericCell(row.getCell(detailedBudgetCol), rfqData?.rfqBudget ?? "-");

  //  Budget Amount (NEW)
  setNumericCell(row.getCell(detailedBudgetAmountCol), material?.budgetAmount ?? "-");

  //  Last Rate (shifted to col 9)
  if (material.lastRate) {
    setNumericCell(row.getCell(detailedLastRateCol), material.lastRate);
  } else {
    row.getCell(detailedLastRateCol).value = "-";
    row.getCell(detailedLastRateCol).alignment = { horizontal: "center" };
  }

  //  Last Rate Date (shifted to col 10)
  row.getCell(detailedLastRateDateCol).value = formatDate(material.lastRateDate);
  row.getCell(detailedLastRateDateCol).alignment = { horizontal: "center" };

  //  Lowest Rate (shifted to col 11)
  setNumericCell(row.getCell(detailedLowestRateCol), lowestPrices[material._id]?.minPrice ?? 0);

  row.getCell(detailedLowestRateCol).style = {
    fill: {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFBBF7B3" },
    },
    alignment: {
      horizontal: "right",
      vertical: "middle",
    },
    border: {
      bottom: { style: "thin" },
    },
  };

});

    let startCol = detailedStaticColumnCount + 1;
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * detailedVendorColumnCount;
      const colEnd = colStart + detailedVendorColumnCount - 1;
      const colLetter = sheet.getColumn(colStart).letter;
      const vendorEndLetter = sheet.getColumn(colEnd).letter;
      sheet.mergeCells(`${colLetter}1:${vendorEndLetter}1`);
      sheet.mergeCells(`${colLetter}2:${vendorEndLetter}2`);
      sheet.mergeCells(`${colLetter}5:${vendorEndLetter}5`);
      let budgetDeviationLine = "";
      let budgetDeviationColor;

      if (hasValidBudget(rfqData) && supplier.quotationReceived) {
        const deviation =
          rfqData.rfqBudget -
          (supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0);
        const deviationPercentage = rfqData.rfqBudget
          ? Math.abs((deviation / rfqData.rfqBudget) * 100)
          : 0;
        budgetDeviationColor = deviation < 0 ? "FFFF0000" : "FF008000";

        budgetDeviationLine = `Rs. ${new Intl.NumberFormat(
          "en-IN"
        ).format(Math.abs(deviation))} (${deviationPercentage.toFixed(2)}%) ${
          deviation < 0 ? "▲" : "▼"
        }`;
      }

      const primaryContact = supplier.contactDetails?.[0] || {};
      const supplierName = supplier.businessName || "-";
      const contactLine = `${primaryContact.mobile || "-"} | ${primaryContact.email || "-"}`;
      const businessNameCell = sheet.getCell(`${colLetter}1`);
      const contactCell = sheet.getCell(`${colLetter}2`);
      const deviationCell = sheet.getCell(`${colLetter}5`);

      businessNameCell.value = `${supplierName} (${supplier.vendorQuotations?.rank || "N/A"})`;
      contactCell.value = contactLine;

      [businessNameCell, contactCell].forEach((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
      });

      if (!supplier.quotationReceived) {
        deviationCell.value = "Quotation not submitted";
        deviationCell.font = { italic: true, color: { argb: "FF6B7280" } };
      } else if (budgetDeviationLine) {
        deviationCell.value = budgetDeviationLine;
        deviationCell.font = {
          bold: true,
          color: { argb: budgetDeviationColor },
        };
      } else {
        deviationCell.value = "";
        deviationCell.font = { italic: true, color: { argb: "FF6B7280" } };
      }
      deviationCell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      // Header row
      sheet.getRow(6).getCell(colStart).value = "MRP";
      sheet.getRow(6).getCell(colStart + 1).value = "Discount";
      sheet.getRow(6).getCell(colStart + 2).value = "Net Rate";
      sheet.getRow(6).getCell(colStart + 3).value = "First Rate";
      sheet.getRow(6).getCell(colStart + 4).value = "Negotiation";
      sheet.getRow(6).getCell(colStart + 5).value = "GST";
      sheet.getRow(6).getCell(colStart + 6).value = "Basic Amount";

      // Set column widths
      [10, 9, 13, 13, 13, 8, 17].forEach(
        (w, i) => (sheet.getColumn(colStart + i).width = w)
      );

      detailedMaterialRows.forEach(({ material, itemRow, vendorRemarksRow, vendorRemarks }) => {
        const row = sheet.getRow(itemRow);

        if (supplier.quotationReceived) {
          const lastQuotationSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
          const firstQuotationSet =
            supplier.vendorQuotations?.quotationSets?.[0];
          const quotedMaterial = lastQuotationSet?.materialDetails?.find(
            (m) => m.id === material._id
          );
          const firstQuotedMaterial = firstQuotationSet?.materialDetails?.find(
            (m) => m.id === material._id
          );

          if (quotedMaterial) {
            // MRP (Base Price)
            setNumericCell(row.getCell(colStart), quotedMaterial.basePrice);

            // Discount
            const discount = parseFloat(quotedMaterial.discount) || 0;
            setNumericCell(row.getCell(colStart + 1), discount);
            row.getCell(colStart + 1).numFmt = "0.00%";
            row.getCell(colStart + 1).value = discount / 100;

            // Net Rate
            const netRate =
              quotedMaterial.basePrice -
              (quotedMaterial.basePrice * quotedMaterial.discount) / 100;
            setNumericCell(row.getCell(colStart + 2), netRate);

            // First Rate
            const firstSupplierPrice = firstQuotedMaterial
              ? firstQuotedMaterial.basePrice -
                (firstQuotedMaterial.basePrice * firstQuotedMaterial.discount) /
                  100
              : 0;
            setNumericCell(
              row.getCell(colStart + 3),
              firstSupplierPrice > 0 ? firstSupplierPrice : "-"
            );

            // Negotiation
            const percentageDifference = calculatePercentageDifference(
              firstSupplierPrice,
              netRate
            );
            const negotiationCell = row.getCell(colStart + 4);
            negotiationCell.value = Math.abs(percentageDifference);
            const displayDifference = Math.abs(percentageDifference);
            setNumericCell(negotiationCell, displayDifference);

            if (percentageDifference < 0) {
              negotiationCell.font = { color: { argb: "FF00AA00" } };
            } else if (percentageDifference > 0) {
              negotiationCell.font = { color: { argb: "FFFF0000" } };
            }

            // GST
            const gst = parseFloat(quotedMaterial.gst) || 0;
            row.getCell(colStart + 5).value = gst / 100;
            row.getCell(colStart + 5).numFmt = "0.00%";
            row.getCell(colStart + 5).alignment = { horizontal: "right" };

            // Basic Amount (Price Without GST)
            setNumericCell(
              row.getCell(colStart + 6),
              quotedMaterial.priceWithoutGst
            );

            // Highlight if lowest price
            const isLowest =
              lowestPrices[material._id]?.minSupplierIndexes.includes(
                supplierIndex
              );
            if (isLowest) {
              row.getCell(colStart + 2).style = {
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFBBF7B3" },
                },
                alignment: {
                  horizontal: "right",
                  vertical: "middle",
                },
              };
            }
          }
        } else {
          // For vendors who didn't submit quotations, leave all cells empty with "-"
          for (let i = 0; i < detailedVendorColumnCount; i++) {
            row.getCell(colStart + i).value = "-";
            row.getCell(colStart + i).alignment = { horizontal: "center" };
          }
        }

        if (vendorRemarksRow) {
          const startLetter = sheet.getColumn(colStart).letter;
          const endLetter = sheet.getColumn(colEnd).letter;
          sheet.mergeCells(`${startLetter}${vendorRemarksRow}:${endLetter}${vendorRemarksRow}`);
          const remarkCell = sheet.getCell(`${startLetter}${vendorRemarksRow}`);
          remarkCell.value = vendorRemarks[supplierIndex] || "";
          remarkCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
          remarkCell.font = { italic: true };
        }
      });

    });

    sheet.getRow(1).height = 22;
    sheet.getRow(2).height = 22;
    sheet.getRow(3).height = 22;

    const detailedHeaderRow = sheet.getRow(6);
    detailedHeaderRow.height = 42;
    for (let columnIndex = 1; columnIndex <= sheet.columnCount; columnIndex += 1) {
      const cell = detailedHeaderRow.getCell(columnIndex);
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    }

    detailedMaterialRows.forEach(({ vendorRemarksRow, vendorRemarks }) => {
      if (!vendorRemarksRow) return;

      sheet.mergeCells(`B${vendorRemarksRow}:${detailedStaticEndLetter}${vendorRemarksRow}`);
      const labelCell = sheet.getCell(`B${vendorRemarksRow}`);
      // labelCell.value = "Vendor Remarks:";
      labelCell.font = { bold: true, italic: true };
      labelCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
      sheet.getRow(vendorRemarksRow).height = getWrappedTextHeight(vendorRemarks, {
        mergedCols: detailedVendorColumnCount,
        columnWidth: 12,
        minHeight: 24,
        lineHeight: 15,
      });
    });

    const materialCostRowIndex = detailedCurrentRow + 1;
    const materialCostRow = sheet.getRow(materialCostRowIndex);
    sheet.mergeCells(`A${materialCostRowIndex}:${detailedStaticEndLetter}${materialCostRowIndex}`);
    materialCostRow.getCell(1).value = "Material Cost:";
    materialCostRow.getCell(1).font = { bold: true };
    materialCostRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * detailedVendorColumnCount;
      const cost =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.totalWithoutGst ?? 0;
      setNumericCell(materialCostRow.getCell(colStart + 6), cost); // Changed from +4 to +6
    });

    let commercialDiscountRowOffset = 0;
    if (hasCommercialDiscount(rfqData, sortedSuppliers)) {
      commercialDiscountRowOffset = 1;
      const rowIndex = materialCostRowIndex + commercialDiscountRowOffset;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:${detailedStaticEndLetter}${rowIndex}`);
      row.getCell(1).value = "Commercial Discount:";
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      row.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFF2CC" },
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = startCol + supplierIndex * detailedVendorColumnCount;
        const percent = getCommercialDiscountPercent(supplier);
        const amount = getCommercialDiscountAmount(supplier);
        const cell = row.getCell(colStart + 6);

        cell.value = amount > 0
          ? `Rs. ${new Intl.NumberFormat("en-IN", {
              maximumFractionDigits: 2,
            }).format(amount)}${percent > 0 ? ` (${percent}%)` : ""}`
          : "-";
        cell.alignment = { horizontal: "right", vertical: "middle" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFF2CC" },
        };
      });
    }

    enabledCharges.forEach((charge, index) => {
      const rowIndex = materialCostRowIndex + commercialDiscountRowOffset + index + 1;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:${detailedStaticEndLetter}${rowIndex}`);
      row.getCell(1).value = charge.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = startCol + supplierIndex * detailedVendorColumnCount;
        const chargeMRP =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;
        setNumericCell(row.getCell(colStart + 6), chargeMRP); // Changed from +4 to +6
      });
    });

    // Add Summary Rows (Basic Amount, GST, Total, Payment Terms)
    const summaryStartRowIndex =
      materialCostRowIndex + commercialDiscountRowOffset + enabledCharges.length + 1;
    const supplierDetailsLabels = [
      {
        label: "Total Basic Amount:",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithoutGst,
      },
      {
        label: "Total GST:",
        getValue: (s) => {
          const withoutGst = s?.vendorQuotations?.grandTotalWithoutGst || 0;
          const withGst = s?.vendorQuotations?.grandTotalWithGst || 0;
          const gstAmount = withGst - withoutGst;
          return isNaN(gstAmount) ? 0 : gstAmount;
        },
      },
      {
        label: "Total Quotation Amount (Inc. GST):",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithGst,
      },
    ];

    supplierDetailsLabels.forEach((detail, index) => {
      const rowIndex = summaryStartRowIndex + index;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:${detailedStaticEndLetter}${rowIndex}`);
      row.getCell(1).value = detail.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = startCol + supplierIndex * detailedVendorColumnCount;
        const value = detail.getValue(supplier);
        setNumericCell(row.getCell(colStart + 6), value); // Changed from +4 to +6
      });
    });

    // Add "Difference from Lowest Quote"
    const diffRowIndex = summaryStartRowIndex + supplierDetailsLabels.length;
    const diffRow = sheet.getRow(diffRowIndex);
    sheet.mergeCells(`A${diffRowIndex}:${detailedStaticEndLetter}${diffRowIndex}`);
    diffRow.getCell(1).value = "Difference from Lowest Quote:";
    diffRow.getCell(1).font = { bold: true };
    diffRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    const l1Total =
      sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * detailedVendorColumnCount;
      const grandTotal = supplier.vendorQuotations?.grandTotalWithoutGst ?? 0;

      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + detailedVendorColumnCount - 1).letter;
      const mergeRange = `${startLetter}${diffRowIndex}:${endLetter}${diffRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${diffRowIndex}`);

      if (supplierIndex === 0) {
        cell.value = "Lowest Quote";
        cell.font = { italic: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (!supplier.quotationReceived) {
        // For vendors who didn't submit quotations
        cell.value = "Quotation not Submitted";
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else {
        const deviation = grandTotal - l1Total;
        const deviationPercent = l1Total
          ? ((deviation / l1Total) * 100).toFixed(2)
          : 0;
        cell.value = `Rs. ${new Intl.NumberFormat("en-IN").format(
          deviation
        )} (${deviationPercent}%)`;
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      }
    });

    const negotiationRowIndex = diffRowIndex + 1;
    const negotiationRow = sheet.getRow(negotiationRowIndex);
    sheet.mergeCells(`A${negotiationRowIndex}:${detailedStaticEndLetter}${negotiationRowIndex}`);
    negotiationRow.getCell(1).value = "Negotiation Outcome:";
    negotiationRow.getCell(1).font = { bold: true };
    negotiationRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * detailedVendorColumnCount;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + detailedVendorColumnCount - 1).letter;
      sheet.mergeCells(
        `${startLetter}${negotiationRowIndex}:${endLetter}${negotiationRowIndex}`
      );

      const cell = sheet.getCell(`${startLetter}${negotiationRowIndex}`);
      cell.value = supplier.quotationReceived
        ? getNegotiationOutcome(supplier)
        : "Quotation not Submitted";
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });

    const paymentTermsRowIndex = negotiationRowIndex + 1;
    const paymentRow = sheet.getRow(paymentTermsRowIndex);
    sheet.mergeCells(`A${paymentTermsRowIndex}:${detailedStaticEndLetter}${paymentTermsRowIndex}`);
    paymentRow.getCell(1).value = "Payment Terms:";
    paymentRow.getCell(1).font = { bold: true };
    paymentRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * detailedVendorColumnCount;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + detailedVendorColumnCount - 1).letter;
      const mergeRange = `${startLetter}${paymentTermsRowIndex}:${endLetter}${paymentTermsRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${paymentTermsRowIndex}`);
      const lastQuotationSet =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
      if (lastQuotationSet?.agreedToPaymentTerms) {
        cell.value = htmlToText(rfqData?.paymentTerms) || "-";
      } else {
        cell.value = htmlToText(lastQuotationSet?.counterPaymentTerms) || "-";
      }
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    const vendorNotesRowIndex = paymentTermsRowIndex + 1;
    const notesRow = sheet.getRow(vendorNotesRowIndex);
    sheet.mergeCells(`A${vendorNotesRowIndex}:${detailedStaticEndLetter}${vendorNotesRowIndex}`);
    notesRow.getCell(1).value = "Vendor Notes:";
    notesRow.getCell(1).font = { bold: true };
    notesRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * detailedVendorColumnCount;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + detailedVendorColumnCount - 1).letter;
      const mergeRange = `${startLetter}${vendorNotesRowIndex}:${endLetter}${vendorNotesRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${vendorNotesRowIndex}`);
      cell.value =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    addVerticalSeparators(sheet, {
      lastRow: vendorNotesRowIndex,
      staticEndCol: detailedStaticColumnCount,
      vendorStartCol: startCol,
      vendorColSpan: detailedVendorColumnCount,
      vendorCount: sortedSuppliers.length,
    });

    const toNumber = (value) => {
      if (value === null || value === undefined) return null;

      // If already a number, return it
      if (typeof value === "number") return value;

      // Remove ₹, commas, spaces, Rs., %
      const cleaned = String(value)
        .replace(/₹/g, "")
        .replace(/Rs\./gi, "")
        .replace(/,/g, "")
        .replace(/%/g, "")
        .trim();

      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    };

    const thinBorder = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    const termsSheet = workbook.addWorksheet("Terms & Conditions");

    // Set header and terms content
    termsSheet.getCell("A1").value = "Terms & Conditions";
    termsSheet.getCell("A1").font = { bold: true };
    termsSheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    termsSheet.getCell("A2").value = rfqData?.termsAndConditions?.replace(
      /<[^>]*>/g,
      ""
    );
    termsSheet.getCell("A2").alignment = { wrapText: true };
    termsSheet.getColumn(1).width = 100;

    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = `${rfqData?.name?.substring(0, 25) || "Export"}_${
      rfqData?.rfqType || ""
    }_${rfqData?.rfqId || ""}.xlsx`;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("Excel downloaded successfully!", {
      variant: "success",
      autoHideDuration: 3000,
    });
  } catch (error) {
    console.error("Error generating material comparison Excel:", error);
    enqueueSnackbar("Failed to download Excel file.", {
      variant: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};
